import { useEffect, useRef } from 'react';

const SELETOR_FOCAVEIS =
  'input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

/**
 * Comportamento de teclado de diálogo para overlays que NÃO podem usar o Modal
 * base sem perder o layout próprio (a gaveta lateral do menu e o vídeo do
 * tutorial): fecha no Escape, prende o foco dentro do painel enquanto aberto,
 * devolve o foco a quem abriu e trava a rolagem do fundo.
 *
 * Devolve a ref que deve ser colocada no painel (o elemento com role="dialog").
 */
export default function useDialogoTeclado(aberto, onClose) {
  const painelRef = useRef(null);
  const anterior = useRef(null);
  const fechar = useRef(onClose);

  // Guarda o onClose numa ref: quem chama costuma passar arrow inline, e sem isso
  // o efeito reiniciaria a cada render, roubando o foco do usuário.
  useEffect(() => { fechar.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!aberto) return undefined;

    anterior.current = document.activeElement;
    const overflowAntes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const foco = painelRef.current?.querySelector(SELETOR_FOCAVEIS);
    (foco || painelRef.current)?.focus();

    // Se um modal foi aberto por cima (ex.: assinatura dentro da gaveta), o
    // teclado é dele — o de baixo não deve reagir.
    const temDialogoPorCima = () => {
      const dialogos = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
      return dialogos.length > 0 && dialogos[dialogos.length - 1] !== painelRef.current;
    };

    const onKeyDown = (e) => {
      if (temDialogoPorCima()) return;

      if (e.key === 'Escape') {
        fechar.current?.();
        return;
      }
      if (e.key !== 'Tab') return;

      // Trap: Tab no último volta ao primeiro e Shift+Tab no primeiro vai ao último.
      const focaveis = painelRef.current?.querySelectorAll(SELETOR_FOCAVEIS);
      if (!focaveis || focaveis.length === 0) return;
      const primeiro = focaveis[0];
      const ultimo = focaveis[focaveis.length - 1];
      if (e.shiftKey && document.activeElement === primeiro) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault();
        primeiro.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflowAntes;
      anterior.current?.focus?.();
    };
  }, [aberto]);

  return painelRef;
}
